import React, { useState, useEffect, useContext, useCallback, useRef } from "react";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import PosItemSkeleton from "./PosItemSkeleton";
import AddItem from "../components/Items/addItem";
import { CART_DRAWER_WIDTH } from "./cartLayout";

import { StaffCommissionDetailContext } from "../Context/StaffCommissionDetailsContext";
import {
  AppBar,
  Toolbar,
  Typography,
  Card,
  CardContent,
  IconButton,
  Grid,
  CardMedia,
  InputBase,
  Badge,
  ButtonBase,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  Table,
  TableBody,
  TableHeader,
  TableContainer,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from "@material-ui/core";
import { getAllStaff, getStaffByUser } from "../actions/staffActions";
import { Autocomplete } from "@material-ui/lab";
import { ItemContext } from "../Context/ItemContext";
import NoData from "../components/svgIcons/NoData";
import { useSnackbar } from "../utils/useSnackbar";
import { matchesItemName } from "../utils/itemSearch";
import { useDispatch, useSelector } from "react-redux";
import { useCart } from "../Context/CartContext";
import { useItemSearch } from "../Context/ItemSearchContext";
import { useShift } from "../Context/ShiftContext";
import { useDiscount } from "../Context/DiscountContext";
import { useCurrency } from "../Context/CurrencyContext";
import { getAllItems } from "../actions/itemActions";
import AddIcon from "@material-ui/icons/Add";
import LocalMallOutlinedIcon from "@material-ui/icons/LocalMallOutlined";
import CommissionDialog from "./CommissionDialog";

// Layout shared by the frosted and solid variants of the card's bottom bar,
// so the two can never drift apart in size, radius or padding.
const cardContentBase = (theme) => ({
  position: "absolute",
  bottom: 0,
  left: 0,
  right: 0,
  borderBottomLeftRadius: 12,
  borderBottomRightRadius: 12,
  color: "#fff",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "flex-end",
  minHeight: "44%",
  padding: theme.spacing(0.5, 0.75, 0.75),
  pointerEvents: "none",
  boxSizing: "border-box",
  [theme.breakpoints.down("xs")]: {
    borderBottomLeftRadius: 10,
    borderBottomRightRadius: 10,
    padding: theme.spacing(0.25, 0.5, 0.5),
    minHeight: "46%",
  },
});

const useStyles = makeStyles((theme) => ({
  content: {
    flexGrow: 1,
    padding: theme.spacing(3),
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    marginRight: -CART_DRAWER_WIDTH,
  },
  contentShift: {
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginRight: 0, // When cart is open, no margin
  },

  title: {
    flexGrow: 1,
  },
  hide: {
    display: "none",
  },

  itemCard: {
    position: "relative",
    width: "100%",
    height: "auto",
    /* Compact without making the product grid feel crowded. */
    aspectRatio: "4 / 3",
    marginBottom: 0,
    backgroundColor: "#ffffff",
    cursor: "pointer",
    borderRadius: 12,
    border: "1px solid rgba(226, 232, 240, 0.85)",
    boxShadow: "0 2px 8px rgba(15, 23, 42, 0.06)",
    transition: "transform 0.16s ease, box-shadow 0.16s ease, border-color 0.16s ease",
    "&:hover": {
      transform: "translateY(-2px)",
      borderColor: "#fdba74",
      boxShadow: "0 8px 20px rgba(15, 23, 42, 0.12)",
    },
    [theme.breakpoints.down("xs")]: {
      borderRadius: 10,
    },
  },
  // The backdrop-filter glass on cardContent doesn't reliably respect
  // border-radius clipping when it shares an element with a hover
  // transform (a known Chrome/Safari quirk). Keeping the transform on
  // itemCard above but the overflow/radius clipping on this separate,
  // never-transformed wrapper avoids the glass bleeding past the corners.
  cardInner: {
    position: "absolute",
    inset: 0,
    overflow: "hidden",
    borderRadius: 12,
    isolation: "isolate",
    [theme.breakpoints.down("xs")]: {
      borderRadius: 10,
    },
  },

  itemGrid: {
    display: "flex",
    flexBasis: "50%",
    maxWidth: "50%",
  },
  /* Desktop grid layout classes (auto-adjusted per user selection, default 6x5) */
  gridCols6: {
    [theme.breakpoints.up("md")]: {
      flexBasis: "16.666667%",
      maxWidth: "16.666667%",
    },
  },
  gridCols5: {
    [theme.breakpoints.up("md")]: {
      flexBasis: "20%",
      maxWidth: "20%",
    },
  },
  gridCols4: {
    [theme.breakpoints.up("md")]: {
      flexBasis: "25%",
      maxWidth: "25%",
    },
  },
  gridCols3: {
    [theme.breakpoints.up("md")]: {
      flexBasis: "33.333333%",
      maxWidth: "33.333333%",
    },
  },

  itemButton: {
    width: "100%",
    display: "block",
    borderRadius: 12,
    overflow: "hidden",
    textAlign: "left",
    [theme.breakpoints.down("xs")]: {
      borderRadius: 10,
    },
  },

  cardMedia: {
    position: "absolute",
    inset: 0,
    height: "100%",
    width: "100%",
    background: "linear-gradient(135deg, #f8fafc 0%, #edf2f7 100%)",
  },

  // Sits behind the photo so it also covers a broken image URL, not just an
  // item that never had one.
  cardPlaceholder: {
    position: "absolute",
    inset: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(145deg, #f8fafc 0%, #edf2f7 100%)",
    paddingBottom: "32%",
  },
  cardPlaceholderIconCircle: {
    width: 44,
    height: 44,
    borderRadius: "50%",
    backgroundColor: "#ffffff",
    boxShadow: "0 2px 8px rgba(15, 23, 42, 0.08)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#94a3b8",
    [theme.breakpoints.down("xs")]: {
      width: 36,
      height: 36,
    },
  },
  cardPlaceholderIcon: {
    fontSize: 24,
    [theme.breakpoints.down("xs")]: {
      fontSize: 20,
    },
  },

  // Frosted bar, used only when a photo is actually behind it.
  cardContent: {
    ...cardContentBase(theme),
    background:
      "linear-gradient(180deg, rgba(15, 23, 42, 0) 0%, rgba(15, 23, 42, 0.6) 35%, rgba(15, 23, 42, 0.92) 100%)",
    backdropFilter: "blur(4px)",
    WebkitBackdropFilter: "blur(4px)",
  },

  // No photo means there is no backdrop worth frosting.
  cardContentSolid: {
    ...cardContentBase(theme),
    background:
      "linear-gradient(180deg, rgba(15, 23, 42, 0) 0%, rgba(15, 23, 42, 0.65) 30%, rgba(15, 23, 42, 0.94) 100%)",
    borderTop: "none",
  },

  itemPriceBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(249, 115, 22, 0.96)",
    color: "#ffffff",
    fontSize: "0.78rem",
    fontWeight: 800,
    lineHeight: 1.15,
    padding: "2px 7px",
    borderRadius: 5,
    letterSpacing: "0.01em",
    fontVariantNumeric: "tabular-nums",
    boxShadow: "0 2px 6px rgba(0, 0, 0, 0.25)",
    marginBottom: 3,
    [theme.breakpoints.down("xs")]: {
      fontSize: "0.7rem",
      padding: "1.5px 5px",
      marginBottom: 2,
    },
  },
  itemName: {
    color: "#ffffff",
    fontSize: "0.76rem",
    fontWeight: 600,
    marginBottom: 0,
    lineHeight: 1.2,
    overflow: "hidden",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    maxWidth: "100%",
    textAlign: "center",
    textShadow: "0 1px 3px rgba(0, 0, 0, 0.7)",
    [theme.breakpoints.down("xs")]: {
      fontSize: "0.7rem",
      lineHeight: 1.15,
    },
  },

  // Dedicated Add Item card with modern dashed outline and tactile hover
  addCard: {
    position: "relative",
    width: "100%",
    aspectRatio: "4 / 3",
    borderRadius: 12,
    border: "2px dashed #cbd5e1",
    background: "linear-gradient(145deg, #f8fafc 0%, #f1f5f9 100%)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    boxShadow: "0 2px 6px rgba(15, 23, 42, 0.04)",
    transition: "all 0.18s cubic-bezier(0.4, 0, 0.2, 1)",
    "&:hover": {
      transform: "translateY(-2px)",
      borderColor: "#f97316",
      background: "linear-gradient(145deg, #fff7ed 0%, #ffedd5 100%)",
      boxShadow: "0 8px 18px rgba(249, 115, 22, 0.15)",
      "& $addCardIconWrap": {
        backgroundColor: "#f97316",
        color: "#ffffff",
        transform: "scale(1.08)",
        boxShadow: "0 4px 12px rgba(249, 115, 22, 0.3)",
      },
      "& $addCardLabel": {
        color: "#ea580c",
      },
    },
    [theme.breakpoints.down("xs")]: {
      borderRadius: 10,
    },
  },
  addCardIconWrap: {
    width: 40,
    height: 40,
    borderRadius: "50%",
    backgroundColor: "#ffedd5",
    color: "#ea580c",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 6,
    transition: "all 0.18s ease",
    boxShadow: "0 2px 6px rgba(234, 88, 12, 0.12)",
    [theme.breakpoints.down("xs")]: {
      width: 34,
      height: 34,
      marginBottom: 4,
    },
  },
  addCardIcon: {
    fontSize: 22,
    [theme.breakpoints.down("xs")]: {
      fontSize: 18,
    },
  },
  addCardLabel: {
    fontSize: "0.78rem",
    fontWeight: 700,
    color: "#475569",
    letterSpacing: "0.01em",
    transition: "color 0.18s ease",
    [theme.breakpoints.down("xs")]: {
      fontSize: "0.72rem",
    },
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    minHeight: "60vh",
    padding: theme.spacing(4, 2),
  },
  emptyStateIconButton: {
    width: 96,
    height: 96,
    borderRadius: "50%",
    backgroundColor: "#fff3e6",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: theme.spacing(3),
  },
  emptyStateIcon: {
    fontSize: 44,
    color: "#ef7f1a",
  },
  emptyStateTitle: {
    fontSize: "1.15rem",
    fontWeight: 700,
    color: "#1f2d3d",
    marginBottom: theme.spacing(0.75),
  },
  emptyStateSubtitle: {
    fontSize: "0.9rem",
    color: "#7f8c9a",
    maxWidth: 320,
    marginBottom: theme.spacing(3),
  },
  emptyStateButton: {
    backgroundColor: "#ef7f1a",
    color: "#fff",
    fontWeight: 700,
    textTransform: "none",
    borderRadius: 10,
    padding: theme.spacing(1, 3),
    boxShadow: "0 8px 18px rgba(239, 127, 26, 0.28)",
    "&:hover": {
      backgroundColor: "#d96f17",
      boxShadow: "0 10px 22px rgba(239, 127, 26, 0.36)",
    },
  },
}));

// Extracted so React.memo can skip re-rendering (and re-diffing the
// CardMedia/backdrop-filter/gradient markup underneath) cards whose props
// haven't actually changed - the catalogue can run into the hundreds of
// items, and previously this whole markup was inline in ItemCards' render,
// so EVERY item re-rendered on every ItemCards render (adding something to
// the cart, a staff list refresh, ...) even though only the clicked item's
// own state ever changes. `item` stays referentially stable across those
// re-renders (it comes straight from the Redux items array via .filter(),
// which doesn't clone items), and `onSelect` is a stable ref-backed callback
// (see handleItemSelect below) - so this now actually bails out for the
// items that aren't affected.
const ItemCard = React.memo(function ItemCard({ item, currency, onSelect, gridCols = 6 }) {
  const classes = useStyles();
  const colClass = classes[`gridCols${gridCols}`] || classes.gridCols6;
  return (
    <Grid item xs={6} sm={6} className={`${classes.itemGrid} ${colClass}`}>
      <ButtonBase centerRipple className={classes.itemButton}>
        <Card className={classes.itemCard} onClick={() => onSelect(item)}>
          <div className={classes.cardInner}>
            <div className={classes.cardPlaceholder} aria-hidden="true">
              <div className={classes.cardPlaceholderIconCircle}>
                <LocalMallOutlinedIcon className={classes.cardPlaceholderIcon} />
              </div>
            </div>

            {item.img && (
              <CardMedia
                component="img"
                alt={item.itemName}
                className={classes.cardMedia}
                image={item.img}
                style={{ objectFit: "cover", backgroundColor: "#fff" }}
                onError={(event) => {
                  // Reveal the placeholder underneath rather than
                  // swapping in a letterboxed stand-in image.
                  event.currentTarget.onerror = null;
                  event.currentTarget.style.display = "none";
                }}
              />
            )}

            <CardContent
              className={item.img ? classes.cardContent : classes.cardContentSolid}
            >
              <div className={classes.itemPriceBadge}>
                {currency}
                {/* toLocaleString rather than toCommas/toFixed(2) - those
                    force 2 decimals onto every price, but these cards have
                    always shown clean whole prices (฿10, not ฿10.00); this
                    only adds comma grouping, still showing real cents when
                    a price actually has them. */}
                {Number(item?.units?.sale?.price || 0).toLocaleString(undefined, {
                  maximumFractionDigits: 2,
                })}
              </div>
              <Typography variant="body2" className={classes.itemName}>
                {item.itemName}
              </Typography>
            </CardContent>
          </div>
        </Card>
      </ButtonBase>
    </Grid>
  );
});

const ItemCards = ({
  staffCommissionDetails,
  cartOpen = false,
  isCartPersistent = false,
  isActive = true,
  gridCols = 6,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const dispatch = useDispatch();
  const {
    cartItems,
    handleAddToCart,
    setCartItems,
  } = useCart();
  const { searchQuery, selectedCategory } = useItemSearch();
  const { isShiftBlocked, openShiftRequiredDialog } = useShift();
  const { staffCommissionDetailsArray, setStaffCommissionDetailsArray } =
    useContext(StaffCommissionDetailContext);
  const { itemId, setItemId } = useContext(ItemContext);
  const { clearDiscount } = useDiscount();
  const items = useSelector((state) => state.items.items);
  //console.log(items);
  const staff = useSelector((state) => state.staff.staff);
  // Was: initialized to `items.length === 0`, so a revisit with items
  // already cached from earlier in the session skipped the skeleton
  // entirely and committed the full (large) item grid synchronously on the
  // very first render - on a big catalogue that's exactly the render cost
  // that made navigating back to this screen feel like it hung for a beat.
  // Always starting with the skeleton (cheap to mount) and clearing it a
  // frame later - see the requestAnimationFrame effect below - guarantees
  // the browser paints something immediately, so navigation itself feels
  // instant even while the heavier grid is still being built underneath.
  const [loading, setLoading] = useState(true);
  // console.log(
  //   staff.filter((staff) =>
  //     staff.services.some((service) => service.id === "658ea97fcba8db44101c2ba6")
  //   )
  // );
  const { currency, updateCurrency } = useCurrency();
  const [imageSources, setImageSources] = useState({});
  const [openItem, setOpenItem] = useState(false);
  // No custom options here - the shared useSnackbar default already gives a
  // working close (X) button at bottom-right. The old `options` object this
  // used to pass was a leftover from the pre-react-toastify snackbar library
  // (position: "bottom" isn't a valid react-toastify position, and its
  // style/closeStyle fields were silently ignored) - that's what broke this
  // toast's positioning and close button.
  const [openSnackbar, closeSnackbar] = useSnackbar();
  // Was: an object keyed by every item's _id, with one full <CommissionDialog>
  // instance mounted per item regardless of whether it was open (see below) -
  // only one item's dialog is ever meaningfully open at a time anyway.
  const [commissionDialogOpen, setCommissionDialogOpen] = useState(false);
  const [selectedService, setSelectedService] = useState({});

  const [quantity, setQuantity] = useState(1);

  // const filteredItems = items.filter((item) =>
  //   item.itemName.toLowerCase().includes(searchQuery.toLowerCase())
  // );
  // const filteredItemsByCategory = selectedCategory
  //   ? filteredItems.filter(
  //       (item) => item.category.categoryId === selectedCategory._id
  //     )
  //   : filteredItems;
  // useEffect(() => {
  //   dispatch(getAllItems());
  // }, [dispatch]);


  const normalizedSearchQuery = String(searchQuery || "").trim().toLowerCase();
  const filteredItems = items.filter((item) => {
    if (!item.isActive) return false;

    // Search by either product name or the barcode printed on its label.
    const barcode = String(item.barcode || "").toLowerCase();
    return matchesItemName(item.itemName, normalizedSearchQuery) || barcode.includes(normalizedSearchQuery);
  });
  const filteredItemsByCategory = selectedCategory
    ? filteredItems.filter(
        (item) => item.category?.categoryId === selectedCategory._id
      )
    : filteredItems;

  // getAllItems() now streams the catalog in progressively (first ~60 items,
  // then the rest in the background - see itemActions.js) instead of one
  // request for everything. Clearing the skeleton here as soon as the store
  // actually has items - rather than waiting on the dispatch promise, which
  // only resolves once every page has arrived - is what makes that streaming
  // actually show up sooner instead of still blocking on the full catalog.
  useEffect(() => {
    if (items.length === 0) return;
    // requestAnimationFrame (rather than clearing synchronously) gives the
    // browser a chance to actually paint the skeleton from the mount above
    // before the full grid replaces it, instead of both commits landing
    // together and the skeleton never becoming visible.
    const frame = requestAnimationFrame(() => setLoading(false));
    return () => cancelAnimationFrame(frame);
  }, [items]);

  useEffect(() => {
    if (items.length > 0) {
      // Already loaded from a previous visit - skip the refetch entirely.
      return;
    }
    // The effect above only clears the skeleton once items actually show up
    // in the store - which never happens for a genuinely empty catalog, so
    // without this the skeleton would spin forever instead of settling into
    // the real "no items" empty state.
    dispatch(getAllItems())
      .then(() => setLoading(false))
      .catch(() => setLoading(false));
    // eslint-disable-next-line
  }, [dispatch]);

  useEffect(() => {
    if (staff.length === 0) {
      dispatch(getAllStaff());
    }
    // dispatch(getStaffByUser({ search: user?.result?._id || user.result.googleId }));
    // eslint-disable-next-line
  }, [dispatch]);
  useEffect(() => {
    if (staffCommissionDetails) {
      setStaffCommissionDetailsArray(staffCommissionDetails);
    }
  }, [staffCommissionDetails]);
  // useEffect(() => {
  //   const getCurrentStockQty = (itemId) => async (dispatch) => {

  //     try {
  //       const { data } = await api.getCurrentStockQuantity(id);

  //     } catch (error) {
  //       console.log(error.response);
  //     }
  //   };
  //   getCurrentStockQty();

  // },[cartItems]);

  const handleCommissionDialogOpen = (service) => {
    setSelectedService(service);
    setQuantity(1);
    setCommissionDialogOpen(true);
  };
  const handleCommissionDialogClose = () => {
    setCommissionDialogOpen(false);
  };

  // Stable identity (useCallback + a ref for the actually-current values)
  // so passing this to the memoized ItemCard below doesn't itself defeat
  // the memoization - handleAddToCart/openShiftRequiredDialog are recreated
  // on every render (CartContext/ShiftContext aren't stabilized down to
  // that level), so without this every ItemCard would re-render whenever
  // either changed, e.g. every time an item is added to the cart.
  const itemSelectHandlersRef = useRef();
  itemSelectHandlersRef.current = {
    isShiftBlocked,
    openShiftRequiredDialog,
    handleAddToCart,
    quantity,
    handleCommissionDialogOpen,
  };
  const handleItemSelect = useCallback((item) => {
    const {
      isShiftBlocked,
      openShiftRequiredDialog,
      handleAddToCart,
      quantity,
      handleCommissionDialogOpen,
    } = itemSelectHandlersRef.current;
    if (item.isStaffReq) {
      handleCommissionDialogOpen(item);
    } else if (isShiftBlocked) {
      openShiftRequiredDialog(item, quantity);
    } else {
      handleAddToCart(item, quantity);
    }
  }, []);

  const handleBarcodeScan = (scannedValue) => {
    // Find the item with a matching barcode
    const matchingItem = items.find(
      (item) => item.barcode === scannedValue || item._id === scannedValue
    );
    if (matchingItem) {
      if (isShiftBlocked) {
        openShiftRequiredDialog(matchingItem, 1);
      } else {
        handleAddToCart(matchingItem, 1);
      }
    } else {
      // 3000 = auto-close after 3 seconds specifically for this toast,
      // without changing the shared hook's default duration for every other
      // openSnackbar call in this file.
      openSnackbar("Item not found for the scanned barcode. Please check.", 3000);
    }
  };
  // Kept up to date every render (cheap ref assignment, no effect) so the
  // listener below - registered once - always calls the latest handler
  // instead of a stale one from whenever the effect last ran.
  const handleBarcodeScanRef = useRef(handleBarcodeScan);
  handleBarcodeScanRef.current = handleBarcodeScan;

  // Global keydown listener for barcode-scanner input (fires characters
  // fast, then Enter). Was: no dependency array, so this tore down and
  // re-attached the document listener on every single render of a screen
  // that re-renders often - pure churn. A ref-based buffer lets this run
  // once (mount/unmount only) while still reacting to current cart/shift state.
  // Gated on isActive since PosMainScreen now stays mounted-but-hidden on
  // other routes (see PersistentPosMainScreen.js) - without this, barcode
  // scans would keep adding to the cart even while looking at another page.
  useEffect(() => {
    if (!isActive) return;
    let buffer = "";
    let resetTimer = null;
    function handleKeyDown(e) {
      if (e.keyCode === 13 && buffer.length > 3) {
        e.preventDefault();
        handleBarcodeScanRef.current(buffer);
        buffer = "";
        return;
      }
      if (e.keyCode === 16) {
        return;
      }
      buffer += e.key;
      clearTimeout(resetTimer);
      resetTimer = setTimeout(() => {
        buffer = "";
      }, 100);
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      clearTimeout(resetTimer);
    };
  }, [isActive]);
  if (loading) {
    return (
      <main
        className={`${classes.content} ${(!isCartPersistent || cartOpen) ? classes.contentShift : ""}`}
        style={{ marginLeft: 0 }}
      >
        <Grid container spacing={2}>
          {Array.from({ length: 18 }).map((_, i) => (
            <Grid
              item
              xs={6}
              sm={6}
              key={i}
              className={`${classes.itemGrid} ${classes[`gridCols${gridCols}`] || classes.gridCols6}`}
              style={{
                opacity: 0,
                animation: `posSkeletonFadeIn 0.4s ease forwards`,
                animationDelay: `${i * 45}ms`,
              }}
            >
              <PosItemSkeleton />
            </Grid>
          ))}
        </Grid>
      </main>
    );
  }

  if (filteredItemsByCategory.length === 0) {
    const handleAddClick = () => {
      setItemId(null);
      setOpenItem(true);
    };
    return (
      <main
        className={`${classes.content} ${(!isCartPersistent || cartOpen) ? classes.contentShift : ""}`}
        style={{ marginLeft: 0 }}
      >
        <AddItem
          open={openItem}
          setOpen={setOpenItem}
          itemId={itemId}
          setItemId={setItemId}
        />
        <div className={classes.emptyState}>
          <div className={classes.emptyStateIconButton}>
            <AddIcon className={classes.emptyStateIcon} />
          </div>
          <Typography className={classes.emptyStateTitle}>
            No items in this category yet
          </Typography>
          <Typography className={classes.emptyStateSubtitle}>
            Add your first item here to start selling from this category.
          </Typography>
          <Button
            variant="contained"
            className={classes.emptyStateButton}
            startIcon={<AddIcon />}
            onClick={handleAddClick}
          >
            Add Item
          </Button>
        </div>
      </main>
    );
  }

  return (
    <>
      <AddItem
        open={openItem}
        setOpen={setOpenItem}
        itemId={itemId}
        setItemId={setItemId}
      />
      <main
        className={`${classes.content} ${(!isCartPersistent || cartOpen) ? classes.contentShift : ""}`}
        style={{ marginLeft: 0 }}
      >
        <Grid container spacing={2} className={classes.gridContainer}>
          <Grid
            item
            xs={6}
            sm={6}
            className={`${classes.itemGrid} ${classes[`gridCols${gridCols}`] || classes.gridCols6}`}
          >
            <ButtonBase centerRipple className={classes.itemButton}>
              <Card
                className={classes.addCard}
                onClick={() => {
                  setItemId(null);
                  setOpenItem(true);
                }}
              >
                <div className={classes.addCardIconWrap}>
                  <AddIcon className={classes.addCardIcon} />
                </div>
                <Typography className={classes.addCardLabel}>
                  Add Item
                </Typography>
              </Card>
            </ButtonBase>
          </Grid>
          {filteredItemsByCategory.map((item) => (
            <ItemCard
              key={item._id}
              item={item}
              currency={currency}
              onSelect={handleItemSelect}
              gridCols={gridCols}
            />
          ))}
        </Grid>
      </main>
      {/* One shared dialog for whichever item was last clicked, instead of
          one full CommissionDialog instance per item in the catalogue (every
          one of which subscribes to CartContext/ShiftContext) - see
          ItemCard/handleItemSelect above. */}
      <CommissionDialog
        open={commissionDialogOpen}
        staff={staff}
        staffCommissionDetailEdit={staffCommissionDetails.find(
          (scd) => scd.serviceId === selectedService._id
        )}
        onClose={handleCommissionDialogClose}
        serviceId={selectedService._id}
        service={selectedService}
        openSnackbar={openSnackbar}
      />
    </>
  );
};

export default ItemCards;
